import { baseUrl, encodeEntryNameForURI, getEntryUrl, serverUrl } from "@kaltura/mediaspace-shared-utils";
import { BaseSettingsParams, CategoryData, CategoryType, GenieContextParams, GenieContextType } from "./types";

export function getCategoryUrl(categoryData: CategoryData | undefined): string {
    if (!categoryData) {
        return "";
    }

    switch (categoryData.type) {
        case CategoryType.CHANNEL:
            return `${baseUrl}/channel/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
        case CategoryType.GALLERY:
            return `${baseUrl}/category/${encodeEntryNameForURI(categoryData.name)}/${categoryData.id}`;
        default:
            return "";
    }
}

/**
 * Build the base chat runtime settings shared between Genie and GenieFloater.
 * Callers can spread additional fields (genieServerUrl, analytics, etc.) on top.
 */
export function buildBaseGenieSettings({ ks, partnerId, agentMode, categoryData, categoryUrl }: BaseSettingsParams) {
    const baseSettings = {
        getSourceUrl: ({ entryId, startTime }: { entryId: string; startTime?: number }) => {
            return getEntryUrl(entryId) + (startTime ? `?st=${startTime}` : "");
        },
        shareUrl: {
            queryParam: "mid" as const,
            createUrl: ({ messageId }: { messageId: string }) => {
                const host = serverUrl + (baseUrl ? `/${baseUrl}` : "");
                if (categoryData) {
                    return `${host}/genie/index/index/categoryId/${categoryData.id}?mid=${messageId}`;
                }
                return `${host}/genie?mid=${messageId}`;
            },
        },
        ks,
        partnerId: partnerId.toString(),
        agentMode,
    };

    if (!categoryData) {
        return baseSettings;
    }

    return {
        ...baseSettings,
        customization: {
            breadcrumb: {
                root: {
                    url: categoryUrl,
                    label: categoryData.name,
                },
            },
        },
    };
}

/**
 * Build the context object used in visual settings for both Genie and GenieFloater.
 */
export function getGenieContext({
    entryId,
    categoryData,
    isAncestorType,
}: GenieContextParams): { type: GenieContextType; id: string } | undefined {
    if (entryId) {
        return { type: GenieContextType.ENTRY, id: entryId };
    }
    if (categoryData) {
        const type = isAncestorType ? GenieContextType.ANCESTOR : GenieContextType.CATEGORY;
        return { type, id: String(categoryData.id) };
    }
    return undefined;
}
